import { call, put, takeEvery } from 'redux-saga/effects';
import {
    FETCH_ACTIVITIES_REQUEST,
    FETCH_ACTIVITIES_SUCCESS,
    FETCH_ACTIVITIES_FAILURE,
    FETCH_ACTIVITY_BY_ID_REQUEST,
    FETCH_ACTIVITY_BY_ID_SUCCESS,
    FETCH_ACTIVITY_BY_ID_FAILURE,
    CREATE_ACTIVITY_REQUEST,
    CREATE_ACTIVITY_SUCCESS,
    CREATE_ACTIVITY_FAILURE,
    UPDATE_ACTIVITY_REQUEST,
    UPDATE_ACTIVITY_SUCCESS,
    UPDATE_ACTIVITY_FAILURE,
    DELETE_ACTIVITY_REQUEST,
    DELETE_ACTIVITY_SUCCESS,
    DELETE_ACTIVITY_FAILURE,
    FETCH_ACTIVITIES_QUOTES_REQUEST,
    FETCH_ACTIVITIES_QUOTES_SUCCESS,
    FETCH_ACTIVITIES_QUOTES_FAILURE,
    FETCH_ACTIVITY_QUOTE_BY_ID_REQUEST,
    FETCH_ACTIVITY_QUOTE_BY_ID_SUCCESS,
    FETCH_ACTIVITY_QUOTE_BY_ID_FAILURE,
    CREATE_ACTIVITY_QUOTE_REQUEST,
    CREATE_ACTIVITY_QUOTE_SUCCESS,
    CREATE_ACTIVITY_QUOTE_FAILURE,
    CREATE_POLICY_REQUEST,
    CREATE_POLICY_SUCCESS,
    CREATE_POLICY_FAILURE,
    GENERATE_BULK_TRANSFERS_CSV_REQUEST,
    GENERATE_BULK_TRANSFERS_CSV_SUCCESS,
    GENERATE_BULK_TRANSFERS_CSV_FAILURE,
    GENERATE_BULK_TRANSFERS_CSV_BY_POLICY_CODES_REQUEST,
    GENERATE_BULK_TRANSFERS_CSV_BY_POLICY_CODES_SUCCESS,
    GENERATE_BULK_TRANSFERS_CSV_BY_POLICY_CODES_FAILURE,
    GENERATE_BULK_TRANSFERS_CSV_END_OF_STAY_REQUEST,
    GENERATE_BULK_TRANSFERS_CSV_END_OF_STAY_SUCCESS,
    GENERATE_BULK_TRANSFERS_CSV_END_OF_STAY_FAILURE,
    GENERATE_BULK_TRANSFERS_CSV_END_OF_STAY_BY_POLICY_CODES_REQUEST,
    GENERATE_BULK_TRANSFERS_CSV_END_OF_STAY_BY_POLICY_CODES_SUCCESS,
    GENERATE_BULK_TRANSFERS_CSV_END_OF_STAY_BY_POLICY_CODES_FAILURE,
} from '../actions/dataActions';
import {
    getActivities,
    getActivityById,
    createActivity,
    updateActivity,
    deleteActivity,
    getActivitiesQuotes,
    getActivityQuoteById,
    createActivityQuote,
    createPolicy,
    generateBulkTransfersCsv,
    generateBulkTransfersCsvByPolicyCodes,
    generateBulkTransfersCsvEndOfStay,
    generateBulkTransfersCsvEndOfStayByPolicyCodes,
} from '../../services/api';
import { handleApiError } from '../../utils/errorHandler';

/**
 * Saga pour récupérer toutes les activités avec pagination et filtres
 */
function* fetchActivitiesSaga(action) {
    try {
        const rawParams = action.payload || {};
        const params = {
            skip: rawParams.skip,
            limit: rawParams.limit,
            category: rawParams.category,
            name: rawParams.name,
            activity_start_date_from: rawParams.activity_start_date_from,
            activity_start_date_to: rawParams.activity_start_date_to,
            activity_end_date_from: rawParams.activity_end_date_from,
            activity_end_date_to: rawParams.activity_end_date_to,
            company_name: rawParams.company_name,
            activity_code: rawParams.activity_code,
            reservation_number: rawParams.reservation_number,
            client_first_name: rawParams.client_first_name,
            client_last_name: rawParams.client_last_name,
            client_email: rawParams.client_email,
        };
        const data = yield call(getActivities, params);
        yield put({ type: FETCH_ACTIVITIES_SUCCESS, payload: data });
    } catch (error) {
        const errorMessage = handleApiError(error, 'Erreur lors du chargement des activités');
        yield put({ type: FETCH_ACTIVITIES_FAILURE, payload: errorMessage });
    }
}

/**
 * Saga pour récupérer une activité par ID
 */
function* fetchActivityByIdSaga(action) {
    try {
        const activityId = action.payload;
        const data = yield call(getActivityById, activityId);
        yield put({ type: FETCH_ACTIVITY_BY_ID_SUCCESS, payload: data });
    } catch (error) {
        const errorMessage = handleApiError(error, 'Erreur lors du chargement de l\'activité');
        yield put({ type: FETCH_ACTIVITY_BY_ID_FAILURE, payload: errorMessage });
    }
}

/**
 * Saga pour créer une activité
 */
function* createActivitySaga(action) {
    try {
        const { partnerId, activityData } = action.payload;
        const response = yield call(createActivity, partnerId, activityData);
        const createdActivity = response.activity || response;
        yield put({ type: CREATE_ACTIVITY_SUCCESS, payload: { activity: createdActivity } });
        yield put({ type: FETCH_ACTIVITIES_REQUEST, payload: { skip: 0, limit: 10 } });
    } catch (error) {
        const errorMessage = handleApiError(error, 'Erreur lors de la création de l\'activité');
        yield put({ type: CREATE_ACTIVITY_FAILURE, payload: errorMessage });
        yield put({ type: FETCH_ACTIVITIES_REQUEST, payload: { skip: 0, limit: 10 } });
    }
}

/**
 * Saga pour mettre à jour une activité
 */
function* updateActivitySaga(action) {
    try {
        const { activityId, activityData } = action.payload;
        const response = yield call(updateActivity, activityId, activityData);
        const updatedActivity = response.activity || response;
        yield put({ type: UPDATE_ACTIVITY_SUCCESS, payload: { activity: updatedActivity } });
    } catch (error) {
        const errorMessage = handleApiError(error, 'Erreur lors de la mise à jour de l\'activité');
        yield put({ type: UPDATE_ACTIVITY_FAILURE, payload: errorMessage });
    }
}

/**
 * Saga pour supprimer une activité
 */
function* deleteActivitySaga(action) {
    try {
        const activityId = action.payload;
        yield call(deleteActivity, activityId);
        yield put({ type: DELETE_ACTIVITY_SUCCESS, payload: activityId });
        yield put({ type: FETCH_ACTIVITIES_REQUEST, payload: { skip: 0, limit: 10 } });
    } catch (error) {
        const errorMessage = handleApiError(error, 'Erreur lors de la suppression de l\'activité');
        yield put({ type: DELETE_ACTIVITY_FAILURE, payload: errorMessage });
        yield put({ type: FETCH_ACTIVITIES_REQUEST, payload: { skip: 0, limit: 10 } });
    }
}

/**
 * Saga pour récupérer les quotes d'activités
 */
function* fetchActivitiesQuotesSaga(action) {
    try {
        const params = action.payload || {};
        const data = yield call(getActivitiesQuotes, params);
        yield put({ type: FETCH_ACTIVITIES_QUOTES_SUCCESS, payload: data });
    } catch (error) {
        const errorMessage = handleApiError(error, 'Erreur lors du chargement des quotes d\'activités');
        yield put({ type: FETCH_ACTIVITIES_QUOTES_FAILURE, payload: errorMessage });
    }
}

/**
 * Saga pour récupérer une quote d'activité par ID
 */
function* fetchActivityQuoteByIdSaga(action) {
    try {
        const quoteId = action.payload;
        const data = yield call(getActivityQuoteById, quoteId);
        yield put({ type: FETCH_ACTIVITY_QUOTE_BY_ID_SUCCESS, payload: data });
    } catch (error) {
        const errorMessage = handleApiError(error, 'Erreur lors du chargement de la quote d\'activité');
        yield put({ type: FETCH_ACTIVITY_QUOTE_BY_ID_FAILURE, payload: errorMessage });
    }
}

/**
 * Saga pour créer une quote d'activité
 */
function* createActivityQuoteSaga(action) {
    try {
        const activityId = action.payload;
        const data = yield call(createActivityQuote, activityId);
        yield put({ type: CREATE_ACTIVITY_QUOTE_SUCCESS, payload: data });
    } catch (error) {
        const errorMessage = handleApiError(error, 'Erreur lors de la création de la quote d\'activité');
        yield put({ type: CREATE_ACTIVITY_QUOTE_FAILURE, payload: errorMessage });
    }
}

/**
 * Saga pour créer une policy
 */
function* createPolicySaga(action) {
    try {
        const { quoteId, clientId, policyData } = action.payload;
        const data = yield call(createPolicy, quoteId, clientId, policyData);
        yield put({ type: CREATE_POLICY_SUCCESS, payload: data });
    } catch (error) {
        const errorMessage = handleApiError(error, 'Erreur lors de la création de la policy');
        yield put({ type: CREATE_POLICY_FAILURE, payload: errorMessage });
    }
}

/**
 * Saga pour générer le fichier CSV de virements en masse
 */
function* generateBulkTransfersCsvSaga(action) {
    try {
        const params = action.payload || {};
        const data = yield call(generateBulkTransfersCsv, params);
        yield put({ type: GENERATE_BULK_TRANSFERS_CSV_SUCCESS, payload: data });
    } catch (error) {
        const errorMessage = handleApiError(error, 'Erreur lors de la génération du fichier CSV de virements');
        yield put({ type: GENERATE_BULK_TRANSFERS_CSV_FAILURE, payload: errorMessage });
    }
}

/**
 * Saga pour générer le fichier CSV de virements en masse par codes de police
 */
function* generateBulkTransfersCsvByPolicyCodesSaga(action) {
    try {
        const params = action.payload || {};
        const data = yield call(generateBulkTransfersCsvByPolicyCodes, params);
        yield put({ type: GENERATE_BULK_TRANSFERS_CSV_BY_POLICY_CODES_SUCCESS, payload: data });
    } catch (error) {
        const errorMessage = handleApiError(error, 'Erreur lors de la génération du fichier CSV de virements par codes de police');
        yield put({ type: GENERATE_BULK_TRANSFERS_CSV_BY_POLICY_CODES_FAILURE, payload: errorMessage });
    }
}

/**
 * Saga pour générer le fichier CSV de virements en masse (end of stay)
 */
function* generateBulkTransfersCsvEndOfStaySaga(action) {
    try {
        const params = action.payload || {};
        const data = yield call(generateBulkTransfersCsvEndOfStay, params);
        yield put({ type: GENERATE_BULK_TRANSFERS_CSV_END_OF_STAY_SUCCESS, payload: data });
    } catch (error) {
        const errorMessage = handleApiError(error, 'Erreur lors de la génération du fichier CSV de virements (end of stay)');
        yield put({ type: GENERATE_BULK_TRANSFERS_CSV_END_OF_STAY_FAILURE, payload: errorMessage });
    }
}

/**
 * Saga pour générer le fichier CSV de virements en masse (end of stay) par codes de police
 */
function* generateBulkTransfersCsvEndOfStayByPolicyCodesSaga(action) {
    try {
        const params = action.payload || {};
        const data = yield call(generateBulkTransfersCsvEndOfStayByPolicyCodes, params);
        yield put({ type: GENERATE_BULK_TRANSFERS_CSV_END_OF_STAY_BY_POLICY_CODES_SUCCESS, payload: data });
    } catch (error) {
        const errorMessage = handleApiError(error, 'Erreur lors de la génération du fichier CSV de virements (end of stay) par codes de police');
        yield put({ type: GENERATE_BULK_TRANSFERS_CSV_END_OF_STAY_BY_POLICY_CODES_FAILURE, payload: errorMessage });
    }
}

/**
 * Watcher pour les actions de gestion des activités
 */
export function* watchActivitiesManagementRequests() {
    yield takeEvery(FETCH_ACTIVITIES_REQUEST, fetchActivitiesSaga);
    yield takeEvery(FETCH_ACTIVITY_BY_ID_REQUEST, fetchActivityByIdSaga);
    yield takeEvery(CREATE_ACTIVITY_REQUEST, createActivitySaga);
    yield takeEvery(UPDATE_ACTIVITY_REQUEST, updateActivitySaga);
    yield takeEvery(DELETE_ACTIVITY_REQUEST, deleteActivitySaga);
    yield takeEvery(FETCH_ACTIVITIES_QUOTES_REQUEST, fetchActivitiesQuotesSaga);
    yield takeEvery(FETCH_ACTIVITY_QUOTE_BY_ID_REQUEST, fetchActivityQuoteByIdSaga);
    yield takeEvery(CREATE_ACTIVITY_QUOTE_REQUEST, createActivityQuoteSaga);
    yield takeEvery(CREATE_POLICY_REQUEST, createPolicySaga);
    yield takeEvery(GENERATE_BULK_TRANSFERS_CSV_REQUEST, generateBulkTransfersCsvSaga);
    yield takeEvery(GENERATE_BULK_TRANSFERS_CSV_BY_POLICY_CODES_REQUEST, generateBulkTransfersCsvByPolicyCodesSaga);
    yield takeEvery(GENERATE_BULK_TRANSFERS_CSV_END_OF_STAY_REQUEST, generateBulkTransfersCsvEndOfStaySaga);
    yield takeEvery(GENERATE_BULK_TRANSFERS_CSV_END_OF_STAY_BY_POLICY_CODES_REQUEST, generateBulkTransfersCsvEndOfStayByPolicyCodesSaga);
}

